import React from "react";
import { Flex } from "rebass/styled-components";
import styled, { css, keyframes } from "styled-components";
import { HAPPY_PATH_EVENTS } from "../../@types";
import type { HappyPathEvent } from "../../@types";

type StepperProps = {
  activeIdx: number;
  isError: boolean;
  fillDuration: number;
  activeFill: number | null;
};

export const Stepper = ({
  activeIdx,
  isError,
  fillDuration,
  activeFill,
}: StepperProps) => {
  const lastIdx = HAPPY_PATH_EVENTS.length - 1;

  return (
    <Track>
      {HAPPY_PATH_EVENTS.map((step, i) => {
        const isPast = i < activeIdx;
        const isActive = i === activeIdx && !isError;
        const isErrorNode = i === activeIdx && isError;

        const dotNode = (
          <DotWrapper
            key={`dot-${step}`}
            aria-label={`${STEP_LABELS[step]} — ${
              isErrorNode
                ? "error"
                : isActive
                ? "active"
                : isPast
                ? "complete"
                : "pending"
            }`}
          >
            {isActive && <Halo />}
            <Dot $past={isPast} $active={isActive} $error={isErrorNode} />
          </DotWrapper>
        );

        if (i === 0) return dotNode;

        const segIdx = i - 1;
        const connFilled = i <= activeIdx;
        const isAnimatingSeg =
          !connFilled && i === activeIdx + 1 && activeIdx < lastIdx && !isError;
        const fill = connFilled ? 1 : isAnimatingSeg ? activeFill ?? 1 : 0;
        const smooth = isAnimatingSeg && activeFill != null;

        return (
          <React.Fragment key={step}>
            <ConnectorWrap $weight={segIdx < 3 ? 1 : 2.5}>
              <ConnectorBg />
              <ConnectorFill
                $fill={fill}
                $instant={connFilled}
                $smooth={smooth}
                $duration={fillDuration}
                $error={isErrorNode}
              />
            </ConnectorWrap>
            {dotNode}
          </React.Fragment>
        );
      })}
    </Track>
  );
};

const STEP_LABELS: Record<HappyPathEvent, string> = {
  REQUESTED: "Requested",
  CREATING: "Creating",
  SUBMITTED: "Submitted",
  QUEUED: "Queued",
  CAPTURING: "Recording",
  COMPLETED: "Complete",
};

const pulse = keyframes`
  0%, 100% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.35); filter: brightness(1.3); }
`;

const Track = styled(Flex)`
  align-items: center;
  width: 100%;
  padding: 6px 0;
`;

const DotWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const haloBreath = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(2); opacity: 0; }
`;

const Halo = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.envyAlpha30};
  animation: ${haloBreath} 2s ease-in-out infinite;
  pointer-events: none;
`;

const Dot = styled.div<{
  $past: boolean;
  $active: boolean;
  $error: boolean;
}>`
  /* Pending dots fill the card background (not transparent) to mask the lines tucked under them. */
  background-color: ${({ $past, $active, $error, theme }) =>
    $error
      ? theme.colors.alert
      : $past || $active
      ? theme.colors.envy
      : theme.colors.navy};
  border: 2px solid
    ${({ $past, $active, $error, theme }) =>
      $error
        ? theme.colors.alert
        : $past || $active
        ? theme.colors.envy
        : theme.colors.chalkAlpha40};
  border-radius: 50%;
  flex-shrink: 0;
  height: 8px;
  position: relative;
  transition:
    background-color 0.3s,
    border-color 0.3s;
  width: 8px;
  z-index: 1;
  ${({ $active }) =>
    $active &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `}
`;

const ConnectorWrap = styled.div<{ $weight: number }>`
  flex: ${({ $weight }) => $weight};
  /* Match the dot-row height so the 2px line (centered below) shares the dot axis. */
  height: 12px;
  /* Tuck under the dots so sub-pixel flex rounding can't leave a hairline gap. */
  margin: 0 -4px;
  min-width: 6px;
  position: relative;
`;

const ConnectorBg = styled.div`
  background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 1px;
  height: 2px;
  left: 0;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const ConnectorFill = styled.div<{
  $fill: number;
  $instant: boolean;
  $smooth: boolean;
  $duration: number;
  $error: boolean;
}>`
  background-color: ${({ $error, theme }) =>
    $error ? theme.colors.alert : theme.colors.envy};
  border-radius: 1px;
  height: 2px;
  left: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: ${({ $instant, $smooth, $duration }) =>
    $instant
      ? "none"
      : $smooth
      ? "width 1s linear"
      : `width ${$duration}s ease-in-out`};
  width: ${({ $fill }) => `${$fill * 100}%`};
  ${({ $fill, $instant, $error, theme }) =>
    $fill > 0 &&
    !$instant &&
    !$error &&
    css`
      box-shadow: 0 0 6px 1px ${theme.colors.envyAlpha30};
    `}
`;
